import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Button, Checkbox, Divider, Stack, Typography } from '@mui/material';
import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import ReactGA from 'react-ga4';
import { TRUCK_UNSET_LABEL, unsetColorFor } from 'src/constants/assignmentLabels';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import numberDecorator from 'src/decorators/number.decorator';
import { AllocateHistoryEntity } from 'src/entities/AllocateHistory.entity';
import { AssignTruckEntity } from 'src/entities/AssignTruck.entity';
import { GarageEntity } from 'src/entities/Garage.entity';
import { PlanningsDeliveryEntity } from 'src/entities/PlanningsDelivery.entity';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';
import { PlanningsOperationEntity } from 'src/entities/PlanningsOperation.entity';
import { PlanningsOperationDeliveryByDeliveryIdEntity } from 'src/entities/PlanningsOperationEntitiesWithStatsByDeliveryId.entity';
import { PlanningsTruckEntity } from 'src/entities/PlanningsTruck.entity';
import { PlanningsWorkingStatisticEntity } from 'src/entities/PlanningsWorkingStatistic.entity';
import { SelectedCycle } from 'src/entities/SelectedCycle.entity';
import { TransferRequestEntity } from 'src/entities/transferRequestEntity';
import { useTrucksQuery } from 'src/hooks/useTrucks.query';
import { PlanningsOperationPlace } from 'src/models/PlanningsOperationGroup.model';
import { SelectedStatusVo } from 'src/vo/SelectedStatus.vo';

import PlanningsDeliveriesPresenter from './PlanningsDeliveries.presenter';
import SelectTruckMenuPresenter, { SelectTruckMenuTruckEntity } from './SelectTruckMenu.presenter';

type Props = {
  entity: PlanningsTruckEntity;
  garage: GarageEntity;
  garageData?: GarageEntity[];
  updateDisplayOrderId: (orderId: number) => void;
  mutateDeleteOrdersOperations: (requestOrderIds: number[]) => void;
  openTransferDialog: (entity: TransferRequestEntity) => void;
  isLoading: boolean;
  driverEntities: PlanningsDriverEntity[];
  deliveryEntities: PlanningsDeliveryEntity[];
  operationEntities: PlanningsOperationEntity[];
  truckEntities: PlanningsTruckEntity[];
  updateWorkingStatisticses: (entity: PlanningsWorkingStatisticEntity) => void;
  currentHistoryVersion: string | undefined;
  updateEditDriverId: (driverId: number) => void;
  deliveriesOnTheTruck: PlanningsDeliveryEntity[];
  firstVisitSet: Set<string>;
  latestAllocateHistory: AllocateHistoryEntity | undefined;
  expandAll: boolean;
  requestSingleAlgorithmPlanning: (deliveryId: number, orderOperationIdsForSort: number[], deleteOrderIdsFromOperations: number[], orderOperationCycleIndexes?: { [key: number]: number }) => void;
  startOn: string;
  endOn: string;
  selectedCycleIndexes: SelectedCycle[];
  updateSelectedCycleIndexes: (deliveryId: number, cycleIndexes: number[]) => void;
  planningsOperationDeliveryByDeliveryIdEntity: PlanningsOperationDeliveryByDeliveryIdEntity;
  resetEditPlaces: () => void;
  updateEditPlaces: (deliveryId: number, cycleIndex: number, places: PlanningsOperationPlace[]) => void;
  addEmptyCycle: (deliveryId: number) => void;
  removeEmptyCycle: (deliveryId: number, cycleIndex: number) => void;
  addDeliveryElementRef: (deliveryId: number, element: HTMLElement) => void;
  assignTruckToTemporaryDeliveryRequest: (entity: AssignTruckEntity) => void;
  assignTruckQueue: AssignTruckEntity[];
  currentAssigning: AssignTruckEntity;
  updateEditTruckId: (truckId: number) => void;
  deleteTemporaryDelivery: (deliveryId: number) => void;
}

const PlanningTruckPresenter: FC<Props> = memo(
  (
    {
      entity,
      garage,
      garageData,
      updateDisplayOrderId,
      mutateDeleteOrdersOperations,
      openTransferDialog,
      isLoading,
      driverEntities,
      deliveryEntities,
      operationEntities,
      truckEntities,
      updateWorkingStatisticses,
      currentHistoryVersion,
      updateEditDriverId,
      deliveriesOnTheTruck,
      firstVisitSet,
      latestAllocateHistory,
      expandAll,
      requestSingleAlgorithmPlanning,
      startOn,
      endOn,
      selectedCycleIndexes,
      updateSelectedCycleIndexes,
      planningsOperationDeliveryByDeliveryIdEntity,
      resetEditPlaces,
      updateEditPlaces,
      addEmptyCycle,
      removeEmptyCycle,
      addDeliveryElementRef,
      assignTruckToTemporaryDeliveryRequest,
      assignTruckQueue,
      currentAssigning,
      updateEditTruckId,
      deleteTemporaryDelivery,
    }
  ) => {
    const { data: truckData } = useTrucksQuery(startOn, { enabled: true, });
    const selectedStatusInitialState = 'none';
    const [selectedStatus, setSelectedStatus] = useState<SelectedStatusVo>(selectedStatusInitialState);
    const [assigningIdAndName, setAssigningIdAndName] = useState<{ id: number, name: string }>(null);
    const [isFixed, setIsFixed] = useState(false);

    useEffect(() => {
      setIsFixed(deliveriesOnTheTruck.flatMap((it) => it.operations).some((it) => it.fixed));
    }, [deliveriesOnTheTruck]);

    const driversOnTheTruck = useMemo(() => (
      driverEntities.filter(({ id }) => deliveriesOnTheTruck.map(({ driverId }) => driverId).includes(id))
    ), [deliveriesOnTheTruck, driverEntities]);

    const checkBoxOnClick = useCallback((event: React.MouseEvent) => {
      if (!entity) return;

      event.stopPropagation();
      ReactGA.event('click', { screen_name: SCREEN_NAMES.PLANNING, button_name: `車両チェック ${selectedStatus === 'none' ? 'ON' : 'OFF'}`, label: entity.id });

      const allDeliveryIds = deliveriesOnTheTruck.map((it) => it.id);
      if (selectedStatus === 'none') {
        allDeliveryIds.forEach((it) => {
          const delivery = planningsOperationDeliveryByDeliveryIdEntity[it];
          if (delivery) {
            updateSelectedCycleIndexes(it, delivery.cycles.map((cycl) => cycl.cycleIndex));
          }
        });

        return;
      }

      allDeliveryIds.forEach((it) => updateSelectedCycleIndexes(it, []));
    }, [entity, deliveriesOnTheTruck, planningsOperationDeliveryByDeliveryIdEntity, selectedStatus, updateSelectedCycleIndexes]);

    const checkboxMemo = useMemo(() => (
      <Checkbox
        onClick={checkBoxOnClick}
        indeterminate={selectedStatus === 'some'}
        checked={selectedStatus === 'every'}
        disabled={isLoading}
        size="small"
        sx={{
          m: 0,
          p: 0.3,
        }}
      />
    ), [checkBoxOnClick, selectedStatus, isLoading]);

    const [truckAnchorEl, setTruckAnchorEl] = useState<null | HTMLElement>(null);
    const [truckIdsHasDelivery, setTruckIdsHasDelivery] = useState<number[]>([]);

    const handleTruckMenuOpen = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setTruckAnchorEl(event.currentTarget);
    }, []);

    const handleTruckListClose = useCallback((event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setTruckAnchorEl(null);
    }, []);

    const assignRunnings = useMemo(() => (
      [...assignTruckQueue, currentAssigning].filter((it) => it)
    ), [assignTruckQueue, currentAssigning]);

    const assigning = useMemo(() => assignRunnings.find((it) => it.origTruckId === entity?.id), [assignRunnings, entity]);

    useEffect(() => {
      if (!(currentAssigning && assigningIdAndName && entity)) return;

      if ((assigningIdAndName.id === -1 && entity.temporary) || (assigningIdAndName.id > 0 && assigningIdAndName.name === entity.licensePlateValue)) {
        setAssigningIdAndName(null);
      }
    }, [assigningIdAndName, currentAssigning, entity]);

    const handleTruckClick = useCallback((event: React.MouseEvent, id: number, licensePlateValue: string) => {
      event.preventDefault();
      event.stopPropagation();

      setAssigningIdAndName({ id, name: licensePlateValue });
      assignTruckToTemporaryDeliveryRequest({
        deliveryIds: deliveriesOnTheTruck.map((it) => it.id),
        truckId: id,
        origTruckId: entity.id,
        licensePlateValue,
        driverId: null,
        driverName: null,
        origDriverId: null,
      });
      setTruckAnchorEl(null);
    }, [assignTruckToTemporaryDeliveryRequest, deliveriesOnTheTruck, entity]);

    useEffect(() => {
      setTruckIdsHasDelivery(deliveryEntities.map((it) => it.truckId));
    }, [deliveryEntities]);

    const selectTruckMenuMemo = useMemo(() => (
      <SelectTruckMenuPresenter
        truckAnchorEl={truckAnchorEl}
        onClose={handleTruckListClose}
        onTruckClick={handleTruckClick}
        truckData={truckData as SelectTruckMenuTruckEntity[] || []}
        truckIdsHasDelivery={truckIdsHasDelivery}
        isTemporary={entity?.temporary}
      />
    ), [entity, truckData, handleTruckClick, handleTruckListClose, truckAnchorEl, truckIdsHasDelivery]);

    const capacityLabels = useMemo(() => {
      const maximumLoadingCapacityQuantity = deliveriesOnTheTruck.find(
        (delivery) => delivery.legRunEntity?.maximumLoadingCapacityQuantity != null
      )?.legRunEntity?.maximumLoadingCapacityQuantity;

      return [
        maximumLoadingCapacityQuantity != null ? `数量 ${numberDecorator.formatNumber(maximumLoadingCapacityQuantity)}` : null,
        entity.maximumLoadingCapacityWeightForCalculation
          ? `重量 ${numberDecorator.convertGramToKg(entity.maximumLoadingCapacityWeightForCalculation, 1)}`
          : null,
        entity.loadingPlatformVolumeMm3
          ? `体積 ${numberDecorator.convertMm3ToM3(entity.loadingPlatformVolumeMm3)}`
          : null,
      ].filter((maybe) => maybe);
    }, [deliveriesOnTheTruck, entity]);

    const truckMemo = useMemo(() => (
      <Stack
        direction="row"
        gap={0.5}
      >
        <Stack direction="row">
          <Button
            onClick={handleTruckMenuOpen}
            disabled={!!assigning || isFixed}
            sx={{ p: 0, m: 0, }}
          >
            <Typography
              variant="h5"
              sx={{ color: unsetColorFor(assigningIdAndName?.name ?? (entity.temporary ? TRUCK_UNSET_LABEL : entity.licensePlateValue)) }}
            >
              {assigningIdAndName?.name ?? (entity.temporary ? TRUCK_UNSET_LABEL : entity.licensePlateValue)}
            </Typography>
            <ArrowDropDownIcon />
          </Button>
          {selectTruckMenuMemo}
        </Stack>
        <Stack direction="row">
          <Typography variant="body2">
            {capacityLabels.length > 0 ? `(${capacityLabels.join(', ')})` : ''}
          </Typography>
        </Stack>
      </Stack>
    ), [handleTruckMenuOpen, assigning, entity, assigningIdAndName, selectTruckMenuMemo, isFixed, capacityLabels]);

    useEffect(() => {
      const selectedDeliveryIds = selectedCycleIndexes.map((it) => it.deliveryId);
      if (deliveriesOnTheTruck.every((it) => selectedDeliveryIds.includes(it.id))) {
        setSelectedStatus(
          'every'
        );

        return;
      }

      if (deliveriesOnTheTruck.some((it) => selectedDeliveryIds.includes(it.id))) {
        setSelectedStatus(
          'some'
        );

        return;
      }

      setSelectedStatus(
        'none'
      );
    }, [deliveriesOnTheTruck, selectedCycleIndexes]);

    return (
      <>
        <Stack
          ref={(el) => { deliveriesOnTheTruck.map((it) => it.id).forEach((id) => { addDeliveryElementRef(id, el); }); }}
        >
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            onClick={checkBoxOnClick}
            style={{
              cursor: 'pointer'
            }}
            // ref={(el) => { deliveriesOnTheTruck.forEach((d) => { addDeliveryElementRef(d.id, el); }); }}
          >
            {checkboxMemo}
            {truckMemo}
          </Stack>
          <PlanningsDeliveriesPresenter
            truck={entity}
            garage={garage}
            garageData={garageData}
            driversOnTheTruck={driversOnTheTruck}
            deliveriesOnTheTruck={deliveriesOnTheTruck}
            operationEntities={operationEntities}
            updateDisplayOrderId={updateDisplayOrderId}
            deliveryEntities={deliveryEntities}
            driverEntities={driverEntities}
            truckEntities={truckEntities}
            mutateDeleteOrdersOperations={mutateDeleteOrdersOperations}
            openTransferDialog={openTransferDialog}
            isLoading={isLoading}
            updateWorkingStatisticses={updateWorkingStatisticses}
            currentHistoryVersion={currentHistoryVersion}
            updateEditDriverId={updateEditDriverId}
            firstVisitSet={firstVisitSet}
            latestAllocateHistory={latestAllocateHistory}
            expandAll={expandAll}
            requestSingleAlgorithmPlanning={requestSingleAlgorithmPlanning}
            startOn={startOn}
            endOn={endOn}
            selectedCycleIndexes={selectedCycleIndexes}
            updateSelectedCycleIndexes={updateSelectedCycleIndexes}
            planningsOperationDeliveryByDeliveryIdEntity={planningsOperationDeliveryByDeliveryIdEntity}
            resetEditPlaces={resetEditPlaces}
            updateEditPlaces={updateEditPlaces}
            addEmptyCycle={addEmptyCycle}
            removeEmptyCycle={removeEmptyCycle}
            addDeliveryElementRef={addDeliveryElementRef}
            assignTruckToTemporaryDeliveryRequest={assignTruckToTemporaryDeliveryRequest}
            assignTruckQueue={assignTruckQueue}
            currentAssigning={currentAssigning}
            updateEditTruckId={updateEditTruckId}
            deleteTemporaryDelivery={deleteTemporaryDelivery}
          />
        </Stack>
        <Divider sx={{ height: '1px' }} />
      </>
    );
  }
);

export default PlanningTruckPresenter;
